import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';

// StickyElement pins content to the top of its scroll container with native CSS
// position:sticky — no scroll listener, no measurement, no layout-controls. It
// yields its behavioural regions as contextual components (like `card.title`):
//
//   <StickyElement as |sticky|>
//     <sticky.scrollable>...expanded content, scrolls away...</sticky.scrollable>
//     <sticky.pinned>...content that pins...</sticky.pinned>
//   </StickyElement>
//
// A banner that simply pins uses <sticky.pinned> on its own. A banner that
// morphs puts the big version in <sticky.scrollable> and the slim version in
// <sticky.pinned>; the pinned bar takes over once the scrollable region has
// scrolled off the top. Because the observer only watches the (non-morphing)
// scrollable region, the toggle can't feed back on itself, and the two regions
// never overlap.
//
// Content that doesn't morph but still needs stuck styling pairs <sticky.pinned>
// with a bare <sticky.sentinel />: the pinned region stays in flow and visible
// the whole time, and picks up .sticky-element--sticky once the sentinel scrolls
// past the handoff line.
//
// Arguments:
//   @classes                   extra classes applied to the regions
//   @scrollContainerSelector    selector for the scrolling ancestor; omit for
//                               window scrolling
//   @topOffset                  px below the container top where content pins
//                               (default 0)
//   @handoffOffset              px below the container top where the expanded
//                               region hands off to the pinned bar; set it to
//                               the pinned bar's height for an in-place swap
//                               (defaults to @topOffset)
//   @toggleParentStickyElement  callback invoked with the stuck boolean
export default class StickyElement extends Component {
  @tracked isSticky = false;

  get topOffset() {
    return this.args.topOffset ?? 0;
  }

  get handoffOffset() {
    return this.args.handoffOffset ?? this.topOffset;
  }

  get handoffDelta() {
    return Math.max(this.handoffOffset - this.topOffset, 0);
  }

  get pinStyle() {
    // Only morphing usages have a handoff to close, and only the rule scoped to
    // `:has(.sticky-element__scrollable)` reads the delta. Everywhere else it is
    // always 0, so emit it only when it says something; the CSS falls back to 0.
    let style = `--sticky-top: ${this.topOffset}px;`;
    if (this.handoffDelta) {
      style += ` --sticky-handoff-delta: ${this.handoffDelta}px;`;
    }
    return htmlSafe(style);
  }

  setStuck = (stuck) => {
    if (stuck === this.isSticky) return;
    this.isSticky = stuck;
    this.args.toggleParentStickyElement?.(stuck);
  };
}
